import React from "react";
import useIsLoading from "../../hooks/queryResults/useIsLoading";
import useTeams from "../../hooks/queryResults/useTeams";
import useWeek from "../../hooks/queryResults/useWeek";
import useWeekLineups from "../../hooks/queryResults/useWeekLineups";
import TeamLogo from "../team/TeamLogo";
import Loading from "../ui/Loading";

export default function NflGameBoxScore({ nflGameStatsData }) {
  console.log("nflGameStatsData", nflGameStatsData);
  const { teams, teamsQuery } = useTeams();
  const { selectedWeek } = useWeek();
  const { weekLineupsData, weekLineupsQuery } = useWeekLineups(selectedWeek);

  const isLoading = useIsLoading(
    nflGameStatsData,
    weekLineupsData,
    weekLineupsQuery,
    teams,
    teamsQuery
  );
  if (isLoading) return <Loading />;

  const getTeamSpotForPlayer = (playerId) => {
    const lineupTeams = weekLineupsData.weekLineups.teams;
    const teamIds = Object.keys(lineupTeams);
    for (const teamId of teamIds) {
      const spots = lineupTeams[teamId].spots;
      for (const spot of spots) {
        if (spot.player === playerId) {
          return {
            ...spot,
            team: teamId,
          };
        }
      }
    }
    return null;
  };

  const out = {};

  const categories = [
    {
      propName: "pass",
      sortProp: "yds",
      columns: [
        { label: "Passing", accessor: "name" },
        {
          label: "Cmp/Att",
          accessor: "att",
          title: "Completions/Attempts",
          func: (val) => `${val.comp}/${val.att}`,
        },
        {
          label: "Pct",
          accessor: "pct",
          func: (val) => (val.pct * 100).toFixed(1),
        },
        { label: "Yds", accessor: "yds", title: "Yards" },
        {
          label: "Avg",
          accessor: "avg",
          title: "Yards per attempt",
          func: (val) => val.avg.toFixed(1),
        },
        { label: "TD", accessor: "td", title: "Touchdowns" },
        { label: "Int", accessor: "inter", title: "Interceptions" },
        {
          label: "Rat",
          accessor: "rating",
          title: "Passer rating",
          func: (val) => val.rating.toFixed(1),
        },
      ],
    },
    {
      propName: "rush",
      sortProp: "yds",
      columns: [
        { label: "Rushing", accessor: "name" },
        { label: "Att", accessor: "att" },
        { label: "Yds", accessor: "yds" },
        { label: "Avg", accessor: "avg", func: (val) => val.avg.toFixed(1) },
        { label: "Long", accessor: "long" },
        { label: "TD", accessor: "td" },
      ],
    },
    {
      propName: "rec",
      sortProp: "yds",
      columns: [
        { label: "Receiving", accessor: "name" },
        { label: "Rec", accessor: "rec" },
        { label: "Yds", accessor: "yds" },
        { label: "Avg", accessor: "avg", func: (val) => val.avg.toFixed(1) },
        { label: "Long", accessor: "long" },
        { label: "TD", accessor: "td" },
        { label: "Tar", accessor: "target" },
      ],
    },
    {
      propName: "fum",
      sortProp: "fum",
      columns: [
        { label: "Fumbles", accessor: "name" },
        { label: "Fum", accessor: "fum" },
        { label: "Lost", accessor: "lost" },
        { label: "Rec", accessor: "rec" },
        { label: "TD", accessor: "td", default: 0 },
      ],
    },
    {
      propName: "def",
      sortProp: "tackles",
      columns: [
        { label: "Defense", accessor: "name" },
        { label: "Tckl", accessor: "tackles", title: "Tackles" },
        { label: "Solo", accessor: "tacklesSolo" },
        { label: "TFL", accessor: "tacklesForLoss", title: "Tackles for Loss" },
        { label: "Sacks", accessor: "sacks" },
        // { label: "QBH", accessor: "qbHits", title: "QB Hits" },
        // { label: "PD", accessor: "passDef", title: "Passes defended" },
        { label: "Int", accessor: "inter", default: 0 },
        { label: "Yds", accessor: "interYds", default: 0 },
        { label: "TD", accessor: "defTd" },
      ],
    },
    {
      propName: "kickRet",
      sortProp: "yds",
      columns: [
        { label: "Kick Returns", accessor: "name" },
        { label: "Ret", accessor: "ret" },
        { label: "Yds", accessor: "yds" },
        { label: "Avg", accessor: "avg", func: (val) => val.avg.toFixed(1) },
        { label: "Long", accessor: "long" },
        { label: "TD", accessor: "td" },
      ],
    },
    {
      propName: "puntRet",
      sortProp: "yds",
      columns: [
        { label: "Punt Returns", accessor: "name" },
        { label: "Ret", accessor: "ret" },
        { label: "Yds", accessor: "yds" },
        { label: "Avg", accessor: "avg", func: (val) => val.avg.toFixed(1) },
        { label: "Long", accessor: "long" },
        { label: "TD", accessor: "td" },
      ],
    },
    {
      propName: "kick",
      sortProp: "points",
      columns: [
        { label: "Kicking", accessor: "name" },
        {
          label: "FG",
          accessor: "fgNumMade",
          func: (val) => `${val.fgNumMade}/${val.fgNumAtt}`,
        },
        {
          label: "Made",
          accessor: "fgYdsMade",
          func: (val) =>
            val.fgYdsMade?.length ? val.fgYdsMade.join(",") : "-",
        },
        {
          label: "Missed",
          accessor: "fgYdsMissed",
          func: (val) =>
            val.fgYdsMissed?.length ? val.fgYdsMissed.join(",") : "-",
        },
        {
          label: "Xpt",
          accessor: "xptNumMade",
          func: (val) => `${val.xptNumMade}/${val.xptNumAtt}`,
        },
      ],
    },
    {
      propName: "punt",
      sortProp: "yds",
      columns: [
        { label: "Punting", accessor: "name" },
        { label: "Num", accessor: "num" },
        { label: "Yds", accessor: "yds" },
        { label: "Avg", accessor: "avg" },
        { label: "TB", accessor: "tb" },
        { label: "In20", accessor: "in20" },
        { label: "Long", accessor: "long" },
      ],
    },
  ];

  ["away", "home"].forEach((ah) => {
    const team = nflGameStatsData.nflGameStats[ah];
    const playerIds = Object.keys(team.players);

    categories.forEach((cat) => {
      const targetPlayers = [];
      playerIds.forEach((playerId) => {
        const playerObj = team.players[playerId];
        if (playerObj.hasOwnProperty(cat.propName)) {
          targetPlayers.push(playerObj);
        }
      });

      targetPlayers.sort((a, b) => {
        if (a[cat.propName][cat.sortProp] === b[cat.propName][cat.sortProp])
          return 0;
        return a[cat.propName][cat.sortProp] < b[cat.propName][cat.sortProp]
          ? -1
          : 1;
      });

      if (!out[cat.propName]) out[cat.propName] = {};

      out[cat.propName][ah] = (
        <table style={{ width: "100%" }}>
          <thead>
            <tr
              style={{
                color: "#0078f8",
                backgroundColor: "#eee",
                height: "38px",
              }}
            >
              <th>Tm</th>
              {cat.columns.map((col, i) => {
                const title = col.title || null;
                return (
                  <th key={i} title={title}>
                    {col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {targetPlayers.reverse().map((tp) => {
              const nflPlayer =
                nflGameStatsData.nflGameStats.players[tp.nflPlayer];

              const color = !!nflPlayer.player ? "#000" : "#777";

              let lineupSpot = null;
              if (nflPlayer.player)
                lineupSpot = getTeamSpotForPlayer(nflPlayer.player);

              let teamLogo = null;
              if (lineupSpot) {
                const teamId = lineupSpot.team;
                const team = teams.getTeamById(teamId);
                const opacity = !!lineupSpot.startingPosition ? 1 : 0.25;

                teamLogo = (
                  <div style={{ opacity }}>
                    <TeamLogo team={team} size={"sm"} week={selectedWeek} />
                  </div>
                );
              }

              return (
                <tr key={tp.id} style={{ color, height: "38px" }}>
                  <td>{teamLogo}</td>
                  {cat.columns.map((col, i) => {
                    let acc = col.accessor;
                    let text;
                    let className = null;

                    if (acc === "name") {
                      const fullName =
                        nflPlayer.firstName + " " + nflPlayer.lastName;
                      const shortName =
                        nflPlayer.firstName.charAt(0) +
                        "." +
                        nflPlayer.lastName.substring(0, 12);
                      text = (
                        <span title={fullName}>
                          {nflPlayer.pos}-{shortName}
                        </span>
                      );
                      className = "left";
                    } else if (col.func) {
                      text = col.func(tp[cat.propName]);
                    } else if (
                      !tp[cat.propName].hasOwnProperty(acc) &&
                      col.hasOwnProperty("default")
                    ) {
                      text = col.default;
                    } else {
                      text = tp[cat.propName][acc];
                    }
                    return (
                      <td key={i} className={className}>
                        {text}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      );
    });
  });

  return (
    <div
      style={{
        display: "flex",
        flexFlow: "column nowrap",
        width: "100%",
      }}
    >
      {categories.map((cat, i) => {
        return (
          <div key={i} style={{ display: "flex", flexFlow: "row nowrap" }}>
            {["away", "home"].map((ah, i) => {
              return (
                <div
                  key={i}
                  style={{
                    width: "50%",
                    marginRight: "1%",
                    marginBottom: "2rem",
                  }}
                >
                  {out[cat.propName][ah]}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
